import type { KyInstance } from 'ky';

import React, { createContext, useContext, useMemo, FC } from 'react';

import { getLatestTermsAndConditions, getTermsAndConditions } from '../api/terms-and-conditions';
import { getSubscriptionDowngradeQuestions } from '../api/subscription-downgrade-questions';
import { CommonQueryParamsSingle, CommonQueryParams } from '../api/common';
import { getSupportTicketSubjects } from '../api/support-ticket-subject';
import { getDashboardBanner } from '../api/dashboard-banner';
import { createHttpClient } from '../api/interceptors';
import { getSubscriptions } from '../api/subscription';
import { getContactUs } from '../api/contact-us';
import { getCities } from '../api/city';
import { getZones } from '../api/zone';
import { getFaqs } from '../api/faq';

interface ContentApiProviderProps extends React.PropsWithChildren {
  locale: 'pl' | 'en' | 'ru' | 'uk';
  infoCenterPublicBaseUrl?: string;
  infoCenterPrivateBaseUrl?: string;
  mobilityHubPrivateBaseUrl?: string;
  token?: string;
}

interface ContentApiContextType {
  locale: 'pl' | 'en' | 'ru' | 'uk';
  infoCenterPublicHttpClient?: KyInstance;
  infoCenterPrivateHttpClient?: KyInstance;
  mobilityHubPrivateHttpClient?: KyInstance;
}

const ContentApiContext = createContext<ContentApiContextType | null>(null);

export const ContentApiProvider: FC<ContentApiProviderProps> = ({
  children,
  locale,
  infoCenterPublicBaseUrl,
  infoCenterPrivateBaseUrl,
  mobilityHubPrivateBaseUrl,
  token,
}) => {
  const infoCenterPublicHttpClient = useMemo(
    () => (infoCenterPublicBaseUrl ? createHttpClient(infoCenterPublicBaseUrl) : undefined),
    [infoCenterPublicBaseUrl],
  );

  const infoCenterPrivateHttpClient = useMemo(
    () =>
      infoCenterPrivateBaseUrl ? createHttpClient(infoCenterPrivateBaseUrl, token) : undefined,
    [infoCenterPrivateBaseUrl, token],
  );

  const mobilityHubPrivateHttpClient = useMemo(
    () =>
      mobilityHubPrivateBaseUrl ? createHttpClient(mobilityHubPrivateBaseUrl, token) : undefined,
    [mobilityHubPrivateBaseUrl, token],
  );

  return (
    <ContentApiContext.Provider
      value={{
        infoCenterPrivateHttpClient,
        infoCenterPublicHttpClient,
        mobilityHubPrivateHttpClient,
        locale,
      }}
    >
      {children}
    </ContentApiContext.Provider>
  );
};

export const useInfoCenterPublic = () => {
  const context = useContext(ContentApiContext);
  if (!context) {
    throw new Error('useInfoCenterPublic must be used within a ContentApiProvider');
  }
  const httpClient = context.infoCenterPublicHttpClient;
  if (!httpClient) throw new Error('Info Center Public http client is not initialized');

  const content = useMemo(
    () => ({
      getContactUs: (options?: CommonQueryParamsSingle) =>
        getContactUs(httpClient, { locale: context.locale, ...options }),
      getTermsAndConditions: (options?: CommonQueryParams) =>
        getTermsAndConditions(httpClient, { locale: context.locale, ...options }),
      getLatestTermsAndConditions: (options?: CommonQueryParams) =>
        getLatestTermsAndConditions(httpClient, {
          locale: context.locale,
          ...options,
        }),
    }),
    [httpClient, context.locale],
  );

  return content;
};

export const useInfoCenterPrivate = () => {
  const context = useContext(ContentApiContext);
  if (!context) {
    throw new Error('useInfoCenterPublic must be used within a ContentApiProvider');
  }
  const httpClient = context.infoCenterPrivateHttpClient;
  if (!httpClient) throw new Error('Info Center Private http client is not initialized');

  const content = useMemo(
    () => ({
      getFaqs: (options?: CommonQueryParams) =>
        getFaqs(httpClient, { locale: context.locale, ...options }),
      getSupportTicketSubjects: (options?: CommonQueryParams) =>
        getSupportTicketSubjects(httpClient, { locale: context.locale, ...options }),
      getDashboardBanner: (options?: CommonQueryParams) =>
        getDashboardBanner(httpClient, { locale: context.locale, ...options }),
    }),
    [httpClient, context.locale],
  );

  return content;
};

export const useMobilityHubPrivate = () => {
  const context = useContext(ContentApiContext);
  if (!context) {
    throw new Error('useInfoCenterPublic must be used within a ContentApiProvider');
  }

  const httpClient = context.mobilityHubPrivateHttpClient;
  if (!httpClient) throw new Error('Mobility Hub Private http client is not initialized');

  const content = useMemo(
    () => ({
      getCities: (options?: CommonQueryParams) =>
        getCities(httpClient, { locale: context.locale, ...options }),
      getZones: (options?: CommonQueryParams) =>
        getZones(httpClient, { locale: context.locale, ...options }),
      getSubscriptions: (options?: CommonQueryParams) =>
        getSubscriptions(httpClient, { locale: context.locale, ...options }),
      getSubscriptionDowngradeQuestions: (options?: CommonQueryParams) =>
        getSubscriptionDowngradeQuestions(httpClient, {
          locale: context.locale,
          ...options,
        }),
    }),
    [httpClient, context.locale],
  );

  return content;
};
